/**
 * ドロップダウンメニュー（チェックボックスバージョン）
 */

import { useEffect, useRef, useState } from 'react';

interface Option {
  value: string;
  label: React.ReactNode; // JSX要素を受け入れられるように変更
}

interface DropdownCheckboxProps {
  options: Option[];
  onSelect: (selectedOptions: Option[]) => void; // 複数選択に対応
  placeholder?: string;
  menuWidth?: number;
  allSelectedText?: string;
  selectedCountText?: (count: number) => string;
  initialSelectedOptions?: Option[];
  autoSelectAll?: boolean;
  className?: string;
  dropdownWidth?: number;
}

const DropdownCheckbox: React.FC<DropdownCheckboxProps> = ({
  options,
  onSelect,
  menuWidth = 160,
  placeholder = '選択してください',
  allSelectedText,
  selectedCountText,
  initialSelectedOptions = [],
  autoSelectAll = false,
  className,
  dropdownWidth,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isInitialized, setIsInitialized] = useState(false);
  const prevInitialSelectedOptionsRef = useRef<Option[]>([]);

  useEffect(() => {
    if (!isInitialized) {
      if (autoSelectAll) {
        setSelectedOptions(options);
      } else {
        setSelectedOptions(initialSelectedOptions);
      }
      prevInitialSelectedOptionsRef.current = initialSelectedOptions;
      setIsInitialized(true);
    }
  }, [options, initialSelectedOptions, autoSelectAll, isInitialized]);

  useEffect(() => {
    if (isInitialized && !autoSelectAll) {
      const prevOptions = prevInitialSelectedOptionsRef.current;
      const hasParentChange =
        prevOptions.length !== initialSelectedOptions.length ||
        !prevOptions.every((prevOption) =>
          initialSelectedOptions.some((newOption) => newOption.value === prevOption.value),
        );

      if (hasParentChange) {
        setSelectedOptions(initialSelectedOptions);
        prevInitialSelectedOptionsRef.current = initialSelectedOptions;
      }
    }
  }, [initialSelectedOptions, isInitialized, autoSelectAll]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option: Option) => {
    let newSelectedOptions: Option[];
    if (option.value === 'all') {
      // 「すべての項目」が選択された場合、すべてを選択または選択解除
      newSelectedOptions = selectedOptions.length === options.length ? [] : [...options];
    } else {
      // 通常の項目の選択または選択解除
      if (selectedOptions.find((opt) => opt.value === option.value)) {
        newSelectedOptions = selectedOptions.filter((selected) => selected.value !== option.value);
      } else {
        newSelectedOptions = [...selectedOptions, option];
      }
    }

    // 「すべての項目」の自動チェック/アンチェック
    const allOption = options.find((opt) => opt.value === 'all');
    if (allOption) {
      if (
        newSelectedOptions.length === options.length - 1 &&
        !newSelectedOptions.includes(allOption)
      ) {
        newSelectedOptions.push(allOption);
      } else if (newSelectedOptions.length < options.length) {
        newSelectedOptions = newSelectedOptions.filter((opt) => opt.value !== 'all');
      }
    }

    setSelectedOptions(newSelectedOptions);
    onSelect(newSelectedOptions);
  };

  const isOptionSelected = (option: Option) => {
    return selectedOptions.some((selected) => selected.value === option.value);
  };

  const getDisplayText = () => {
    if (selectedOptions.length === 0) {
      return allSelectedText || placeholder;
    }

    if (selectedOptions.length === options.length && allSelectedText) {
      return allSelectedText;
    }

    if (selectedCountText) {
      return selectedCountText(selectedOptions.length);
    }
  };

  const displayText = getDisplayText();

  return (
    <div
      ref={dropdownRef}
      className='relative w-full'
    >
      {/* ドロップダウンボタン */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex w-full items-center justify-between rounded-md border border-gray-200 bg-white px-6 py-[10px] text-left text-sm ${className}`}
      >
        <span className='truncate'>{displayText}</span>
        <div
          className='h-[5px] w-[10px] bg-black transition-transform'
          style={{
            clipPath: 'polygon(50% 100%, 0 10%, 100% 10%)',
          }}
        />
      </button>

      {/* ドロップダウンメニュー */}
      {isOpen && (
        <div
          className='absolute z-50 mt-1 overflow-hidden rounded-md border border-gray-200 bg-white text-sm'
          style={{
            width: `${dropdownWidth || menuWidth}px`,
          }}
        >
          <div className='max-h-[182px] min-h-1 overflow-y-auto'>
            {options.map((option) => (
              <label
                key={option.value}
                className='flex w-full cursor-pointer items-center gap-2 overflow-hidden px-6 py-[10px] transition-colors hover:bg-gray-50'
              >
                <input
                  type='checkbox'
                  checked={isOptionSelected(option)}
                  onChange={() => handleSelect(option)}
                  className='checkbox !size-4'
                />
                <span className='flex-1 truncate'>{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownCheckbox;
